/* External Deps */
import { FC, ReactElement, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";

/* Internal Deps */
import { Dropzone } from "@/components";
import { useSnackbar } from "@/hooks";

interface ExcelUploadProps {
  onFileUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  disabled?: boolean;
}

const ExcelUpload: FC<ExcelUploadProps> = ({ onFileUpload, isUploading, disabled = false }): ReactElement => {
  const { show } = useSnackbar();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleFileSelect = async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];
    
    if (!allowedTypes.includes(file.type)) {
      show(
        "Invalid file type",
        "Please upload a valid Excel file (.xlsx or .xls)",
        "error"
      );
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      show(
        "File too large",
        "File size must be less than 5MB",
        "error"
      );
      return;
    }

    setUploadedFile(file);
    
    try {
      await onFileUpload(file);
    } catch (error) {
      console.error("Error uploading file:", error);
      show(
        "Upload failed",
        "An error occurred while uploading the file. Please try again.",
        "error"
      );
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Excel File Upload
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload your Excel file to extract certificate of origin data using AI processing.
      </Typography>

      {isUploading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            textAlign: "center",
            border: 2,
            borderColor: "primary.main",
            borderStyle: "dashed",
            borderRadius: 2,
            p: 4,
            bgcolor: "background.paper",
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="primary">
            Processing file...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we extract the data from your Excel file.
          </Typography>
        </Box>
      ) : (
        <Dropzone
          onUpload={async (file: File) => {
            await handleFileSelect([file]);
          }}
          accept={[
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
          ]}
          extensions={['xlsx', 'xls']}
          fileFormat="Excel"
          maxSize={5 * 1024 * 1024} // 5MB
          multiple={false}
          disabled={isUploading || disabled}
        />
      )}

      {uploadedFile && (
        <Typography variant="body2" sx={{ mt: 2, color: "success.main", textAlign: "center" }}>
          Selected: {uploadedFile.name}
        </Typography>
      )}
    </Box>
  );
};

export default ExcelUpload;